import { AppError } from "config/errors";
import {
  IUpdateEstimateDatumInput,
  updateEstimationDatumMutation,
  updatePrequalificationInformationMutation,
} from "gql/mutations/estimate";
import { fetchIneligibleForCoverageReasons } from "gql/queries/account";
import {
  adjustEstimateQuery,
  fetchCurrentUserEstimate,
  IAdjustCoverageInput,
} from "gql/queries/estimate";
import { RootState } from "store";
import { selectConstellation } from "store/selectors";
import { getConstellationMembership } from "utils/constellation";

import { createAsyncThunk } from "@reduxjs/toolkit";
import { isFinite } from "lodash-es";

/* ------------       THUNKS      ------------------ */

export const adjustEstimate = createAsyncThunk(
  "estimate/adjustEstimate",
  async (input: IAdjustCoverageInput, { rejectWithValue }) => {
    try {
      const roundInputAmount =
        input.amount && isFinite(input.amount)
          ? { ...input, amount: Math.floor(input.amount) }
          : input;

      const response = await adjustEstimateQuery(roundInputAmount);

      return response.data.adjustEstimate;
    } catch (err) {
      return rejectWithValue(AppError.from(err).toPlainObject());
    }
  }
);

export const updateEstimate = createAsyncThunk(
  "estimate/updateEstimate",
  async (input: IUpdateEstimateDatumInput, { rejectWithValue }) => {
    try {
      const { data } = await updateEstimationDatumMutation(input);
      const updatedPayload = { ...input, ...data.updateEstimateDatum };

      return updatedPayload;
    } catch (err) {
      return rejectWithValue(AppError.from(err).toPlainObject());
    }
  }
);

export const getCurrentUserEstimate = createAsyncThunk(
  "estimate/getCurrentUserEstimate",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await fetchCurrentUserEstimate();
      const account = getConstellationMembership(data.me.activeConstellations, "wysh-insurance");

      return {
        coverageDetails: account?.coverageDetails,
        latestEstimateDatum: account?.latestEstimateDatum,
      };
    } catch (err) {
      return rejectWithValue(AppError.from(err).toPlainObject());
    }
  }
);

export const getIneligibleForCoverageReasons = createAsyncThunk(
  "estimate/getIneligibleForCoverageReasons",
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchIneligibleForCoverageReasons();

      return response.data.userIneligibleReasons.ineligibleCoverageReasons;
    } catch (err) {
      return rejectWithValue(AppError.from(err).toPlainObject());
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState() as RootState;
      const constellation = selectConstellation(state);

      if (!constellation.id) {
        return false;
      }
    },
  }
);

export const updatePrequalificationInformation = createAsyncThunk(
  "estimate/updatePrequalificationInformation",
  async (
    input: Parameters<typeof updatePrequalificationInformationMutation>[0],
    { rejectWithValue }
  ) => {
    try {
      const { data } = await updatePrequalificationInformationMutation(input);

      return data.updatePrequalificationInformation?.response;
    } catch (err) {
      return rejectWithValue(AppError.from(err).toPlainObject());
    }
  }
);
